import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase.js";
import { apiFetch } from "../services/api.js";
import { Logo } from "../atoms/Logo.jsx";

/**
 * EMAIL-5 / AUTH-18: Magic link callback handler.
 *
 * Supports two URL shapes:
 *
 *   1. AUTH-18 token-hash flow (preferred — keeps the clickable link on the
 *      app domain for URL-domain alignment with the Resend sender):
 *        /auth/callback?token_hash=<hash>&type=magiclink
 *      We call supabase.auth.verifyOtp({ token_hash, type }) to exchange
 *      the one-time hash for a session. The email template shipped by
 *      AUTH-17 uses this URL shape.
 *
 *   2. Legacy hash-fragment flow (fallback for in-flight links that were
 *      sent before the Supabase template was updated):
 *        /auth/callback#access_token=…&refresh_token=…&type=magiclink
 *      The Supabase JS SDK auto-parses the hash and sets the session; we
 *      just wait for SIGNED_IN.
 *
 * After a session exists (either path), we:
 * 1. Call link-voter to connect the auth user to a voter record
 * 2. Check role — admin/PO users route to admin flow, citizens to onboarding
 * 3. Redirect accordingly (onVerified for citizens, onAdminVerified otherwise)
 *
 * Props:
 *   onVerified(email, voter) — called for citizen users
 *   onAdminVerified(role, me) — called for admin/persona_owner users
 *   onError()                — called on fatal error (expired link, etc.)
 */
export function MagicLinkVerify({ onVerified, onAdminVerified, onError }) {
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    // Copilot #1 (v1.26.4 review) — unmount cleanup. The diagnostic
    // setTimeout AND the onAuthStateChange subscription below both
    // survive unmount if the user navigates away (or the parent
    // re-renders and tears this down) between verifyOtp being called
    // and SIGNED_IN arriving. That leaked a listener AND risked
    // "state update on unmounted component" warnings when the late
    // fire called setStatus / setErrorMsg on a dead component.
    //
    // Capture the timer handle + subscription in closure vars the
    // returned cleanup can see, and a `cancelled` flag so the async
    // callback stops issuing setStatus calls once unmounted.
    let diagnosticTimer = null;
    let authSubscription = null;
    let cancelled = false;

    // Guarded state setters — no-op after unmount so any stray async
    // tail (verifyOtp resolving, a delayed fetch in linkAndProceed)
    // can't reach into a dead component.
    const safeSetStatus = (v) => { if (!cancelled) setStatus(v); };
    const safeSetErrorMsg = (v) => { if (!cancelled) setErrorMsg(v); };

    async function handleCallback() {
      // EMAIL-15: Immediately detect error hash params from Supabase redirect
      // (e.g. #error=access_denied&error_code=otp_expired&error_description=Email+link+is+invalid+or+has+expired)
      const hash = window.location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.replace(/^#/, ""));
        const errorCode = params.get("error_code");
        const errorDesc = params.get("error_description");
        if (errorCode || params.get("error")) {
          const isExpired = errorCode === "otp_expired" || errorDesc?.toLowerCase().includes("expired");
          safeSetStatus("error");
          safeSetErrorMsg(
            isExpired
              ? "This magic link has expired. Please request a new one."
              : errorDesc?.replace(/\+/g, " ") || "This magic link is invalid. Please request a new one."
          );
          return;
        }
      }

      // AUTH-18: token-hash flow. When the email template uses
      // `{{ .SiteURL }}/auth/callback?token_hash=…&type=…`, the link lands
      // here with query params instead of a hash fragment. Exchange the
      // hash for a real session via verifyOtp. The link never touches
      // supabase.co — deliverability reputation stays aligned to our
      // sender domain.
      //
      // Hotfix v1.26.4: `type` defaults to "magiclink" when missing. Some
      // GoTrue versions leave `{{ .EmailActionType }}` unsubstituted or
      // render it empty, producing `?token_hash=X&type=` — the old code
      // gated verifyOtp on `tokenHash && type`, skipped it on empty type,
      // fell through to the 10-second getSession timeout, and surfaced
      // "Magic link expired or invalid" on a perfectly good link. Since
      // every email we send IS a magic link, defaulting is correct and
      // closes the false-expired bug.
      const search = new URLSearchParams(window.location.search);
      const tokenHash = search.get("token_hash");
      const type = search.get("type") || "magiclink";
      let verifyOtpRan = false;
      try {
        if (tokenHash) {
          verifyOtpRan = true;
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });
          if (error) throw error;
          if (data?.session?.user) {
            await linkAndProceed(data.session.user);
            return;
          }
          // verifyOtp succeeded but returned no user. Don't error out —
          // the SDK's own `detectSessionInUrl` may have already consumed
          // the token during init, in which case the session lands via
          // onAuthStateChange shortly. Fall through to the wait-for-
          // SIGNED_IN path below.
        }
      } catch (e) {
        // A thrown error here IS authoritative — the token was rejected
        // by Supabase, not just racing. Show the expired copy and stop.
        //
        // Copilot suppressed #5 — use optional chaining consistently.
        // A non-Error throw (string, null, plain object) would turn
        // `e.message` in the fallback into a TypeError and mask the
        // original failure.
        const msg = (e?.message || "").toLowerCase();
        const isExpired = msg.includes("expired") || msg.includes("invalid");
        safeSetStatus("error");
        safeSetErrorMsg(
          isExpired
            ? "This magic link has expired. Please request a new one."
            : e?.message || "Verification failed. Please try again.",
        );
        return;
      }

      try {
        // If a session already exists (SDK's detectSessionInUrl consumed
        // the token, or we're on the legacy hash-fragment flow), proceed
        // straight away.
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) throw error;

        if (session?.user) {
          await linkAndProceed(session.user);
          return;
        }

        // No session yet. Wait for the SDK to emit SIGNED_IN — either from
        // verifyOtp's async session-hydration path, or from the hash-
        // fragment auto-detect. No hard timeout-as-error: if /candidates
        // and /rewards succeed moments later (bug A symptom, v1.26.3), it
        // means the session DID land and the user ISN'T expired. Surfacing
        // "expired" under that race was the actual regression.
        //
        // We still attach a long diagnostic timer (20 s) that logs to the
        // console if no SIGNED_IN ever arrives — so if a real stuck state
        // happens in prod, we can debug it from the diagnostics without
        // the user being stuck on a spinner forever. On timer fire we do
        // show the error screen, but with a more honest copy.
        // Assign to the outer-scope `diagnosticTimer` — the cleanup
        // function needs to be able to clearTimeout() this on unmount.
        diagnosticTimer = setTimeout(() => {
          // eslint-disable-next-line no-console
          console.warn("[MagicLinkVerify] No SIGNED_IN after 20s", {
            verifyOtpRan,
            hadTokenHash: !!tokenHash,
            rawType: search.get("type"),
            resolvedType: type,
          });
          safeSetStatus("error");
          safeSetErrorMsg(
            verifyOtpRan
              ? "We couldn't complete sign-in. Please request a new link."
              : "Magic link is missing its token. Please request a new one.",
          );
        }, 20000);

        // Assign to the outer-scope `authSubscription` — the cleanup
        // function needs to be able to call .unsubscribe() on unmount.
        const sub = supabase.auth.onAuthStateChange(async (event, newSession) => {
          if (event === "SIGNED_IN" && newSession?.user) {
            clearTimeout(diagnosticTimer);
            diagnosticTimer = null;
            authSubscription?.unsubscribe();
            authSubscription = null;
            if (cancelled) return; // don't touch state after unmount
            await linkAndProceed(newSession.user);
          }
        });
        authSubscription = sub?.data?.subscription ?? null;
      } catch (e) {
        safeSetStatus("error");
        safeSetErrorMsg(e?.message || "Verification failed. Please try again.");
      }
    }

    async function linkAndProceed(user) {
      // Link auth user to voter record
      let voterData = null;
      try {
        const result = await apiFetch("/api/auth/link-voter", { method: "POST" });
        // EMAIL-15: If returning user, link-voter returns the existing voter record
        if (result?.voter) voterData = result.voter;
      } catch {
        // link-voter may fail if voter doesn't exist yet — that's OK,
        // voter record is created on location step via handleOnboardComplete.
      }

      // Check if this was an admin/PO magic link login — detect role via /api/auth/me
      const isAdminIntent = sessionStorage.getItem("qm_admin_magic_link") === "1";
      if (isAdminIntent && onAdminVerified) {
        sessionStorage.removeItem("qm_admin_magic_link");
        try {
          const me = await apiFetch("/api/auth/me");
          if (me.role === "admin" || me.role === "persona_owner") {
            if (cancelled) return;
            safeSetStatus("success");
            setTimeout(() => { if (!cancelled) onAdminVerified(me.role, me); }, 800);
            return;
          }
          // Not an admin/PO — fall through to citizen flow
        } catch {
          // /api/auth/me failed — fall through to citizen flow
        }
      }
      // Clean up flag if it was set
      sessionStorage.removeItem("qm_admin_magic_link");

      if (cancelled) return;
      safeSetStatus("success");
      const email = user.email;
      // Brief success state then proceed — pass voter data for returning users
      setTimeout(() => { if (!cancelled) onVerified(email, voterData); }, 800);
    }

    handleCallback();

    // Cleanup — fires on actual unmount (user navigates away before
    // verification completes). Idempotent: the SIGNED_IN handler above
    // already clears the timer and unsubscribes on the happy path, so
    // this just covers the "user navigated away" case. Setting
    // `cancelled = true` short-circuits any pending async tail
    // (verifyOtp promise, linkAndProceed fetches) so none of them
    // reach into the unmounted component.
    return () => {
      cancelled = true;
      if (diagnosticTimer) {
        clearTimeout(diagnosticTimer);
        diagnosticTimer = null;
      }
      if (authSubscription) {
        try { authSubscription.unsubscribe(); } catch { /* SDK quirk */ }
        authSubscription = null;
      }
    };
    // Hotfix v1.26.5: deps MUST stay empty. App.jsx passes inline arrow
    // functions for onVerified / onAdminVerified / onError, so those
    // references change on every parent re-render. With those in the
    // deps, React treats every parent re-render as effect re-run —
    // firing cleanup, flipping `cancelled=true`, unsubscribing from
    // onAuthStateChange, and killing the in-flight verification even
    // though the component is still mounted. `handled.current` already
    // gates the body to run-once, so the deps array isn't doing any
    // useful work either way. The callbacks captured at mount are the
    // ones we call — they're only invoked once at verification
    // completion, so staleness isn't a concern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{minHeight:"100vh",background:"#090e1a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 20px"}}>
      <div style={{width:"100%",maxWidth:400,textAlign:"center",animation:"fadeUp .4s ease"}}>

        <div style={{marginBottom:36}}>
          <div style={{margin:"0 auto 14px",display:"flex",justifyContent:"center"}}><Logo size={32} /></div>
        </div>

        {status === "verifying" && (
          <div style={{animation:"fadeUp .3s ease"}}>
            <div style={{fontSize:40,marginBottom:20,animation:"spin 1.5s linear infinite"}}>
              <span role="img" aria-label="loading">&#x23F3;</span>
            </div>
            <h2 style={{color:"#f1f5f9",fontSize:20,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 10px"}}>Verifying your email...</h2>
            <p style={{color:"#64748b",fontSize:14,lineHeight:1.6}}>Just a moment while we confirm your identity.</p>
          </div>
        )}

        {status === "success" && (
          <div style={{animation:"fadeUp .3s ease"}}>
            <div style={{fontSize:48,marginBottom:20}}>
              <span role="img" aria-label="check">&#x2705;</span>
            </div>
            <h2 style={{color:"#16a34a",fontSize:20,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 10px"}}>Email verified!</h2>
            <p style={{color:"#64748b",fontSize:14,lineHeight:1.6}}>Redirecting you...</p>
          </div>
        )}

        {status === "error" && (
          <div style={{animation:"fadeUp .3s ease"}}>
            <div style={{fontSize:48,marginBottom:20}}>
              <span role="img" aria-label="error">&#x26A0;&#xFE0F;</span>
            </div>
            <h2 style={{color:"#ef4444",fontSize:20,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 10px"}}>Link expired</h2>
            <p style={{color:"#64748b",fontSize:14,margin:"0 0 24px",lineHeight:1.6}}>
              {errorMsg || "This magic link has expired or is invalid."}
            </p>
            <p style={{color:"#475569",fontSize:13,margin:"0 0 24px",lineHeight:1.6}}>
              Enter your email to receive a fresh magic link.
            </p>
            <button
              onClick={() => onError?.()}
              style={{padding:"14px 28px",background:"linear-gradient(135deg,#3b82f6,#2563eb)",color:"#fff",border:"none",borderRadius:11,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
            >
              Request a new link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MagicLinkVerify;
